import { useContext, useEffect, useRef, useState } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIButton from "@mui/material/Button";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIGrid from "@mui/material/Grid";
import MUITableContainer from "@mui/material/TableContainer";
import MUITable from "@mui/material/Table";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUITableBody from "@mui/material/TableBody";
import MUISaveIcon from '@mui/icons-material/Save';
import MUIAddIcon from '@mui/icons-material/Add';
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIStack from "@mui/material/Stack";
import MUITypography from "@mui/material/Typography";
import MUIThumbDownIcon from '@mui/icons-material/ThumbDown';
import MUIThumbUpIcon from '@mui/icons-material/ThumbUp';
import MUIHourglassTopIcon from '@mui/icons-material/HourglassTop';
import MUIReportIcon from '@mui/icons-material/Report';
import MUIErrorIcon from '@mui/icons-material/Error';
import MUICheckIcon from '@mui/icons-material/Check';
import MUIFeedbackIcon from '@mui/icons-material/Feedback';
import * as MUIIcons from "@mui/icons-material";
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';

import EmptyState from "../../EmptyState";
import Card from "../../Card";
import User from "../../../Utility/Crud/User";
import IssueCategory from "../../../Utility/Crud/IssueCategory";
import IssueReason from "../../../Utility/Crud/IssueReason";
import IssueStatus from "../../../Utility/Crud/IssueStatus";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import OrderActivityType from "../../../Utility/Crud/OrderActivityType";
import OrderStatusReason from "../../../Utility/Crud/OrderStatusReason";
import InfoItem from "../../InfoItem";
import Calendar from "../../../Utility/Calendar";

/**
 * CardChartFeedback renders a Card component
 * displaying patient feedback.
 * @returns {JSX.Element}
 */

const title = "Feedback";
const icon = MUIFeedbackIcon;

const className = "CardChartFeedback";

export default function CardChartFeedback(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);
    const [editMode, setEditMode] = useState(false);
    const [cardChart, setCardChart] = useState(null);
    const [patientIssues, setPatientIssues] = useState([]);
    const [patientIssueDetailsView, setPatientIssueDetailsView] = useState(false);
    const [selectedIssueNote, setSelectedIssueNote] = useState(null);

    const [selectedIssue, setSelectedIssue] = useState(null);
    const [followUpDays, setFollowUpDays] = useState(null);

    const [newPatientIssueId, setNewPatientIssueId] = useState(null);
    const [newPatientIsssueNoteId, setNewPatientIsssueNoteId] = useState(null);
    const activeDraftIssueIdRef = useRef(null);

    const patient = chartContext.chart.patient[chartContext.patientId];
    const originalPatientIssue = chartContext.originalChart.patientIssue[selectedIssue?.id || newPatientIssueId];
    const patientIssue = chartContext.chart.patientIssue[selectedIssue?.id || newPatientIssueId];
    const patientIssueReason = mainContext.issueReasonsIndexed[patientIssue?.issueReasonId];
    const issueCategoryId = patientIssueReason?.issueCategoryId ?? null;

    useEffect(() => {
        if (editMode) {
            chartContext.updateValidationRules({
                key: className,
                rules: [
                    {
                        target: "patientIssue",
                        id: newPatientIssueId,
                        field: "issueCategoryId",
                        rule: {
                            "required": true
                        }
                    },
                    {
                        target: "patientIssue",
                        id: newPatientIssueId,
                        field: "issueReasonId",
                        rule: {
                            "required": true
                        }
                    },
                    {
                        target: "patientIssue",
                        id: newPatientIssueId,
                        field: "orderId",
                        rule: {
                            "required": true
                        }
                    },
                    {
                        target: "patientIssue",
                        id: newPatientIssueId,
                        field: "complaintDescription",
                        rule: {
                            "required": true
                        }
                    },
                    ...(issueCategoryId === 3 ? [{
                        target: "patientIssue",
                        id: newPatientIssueId,
                        field: "complimentedUserId",
                        rule: {
                            "required": true
                        }
                    }] : []),
                ]
            });
        }
        else {
            chartContext.updateValidationRules({ key: className, rules: [] });
        }
    }, [editMode, issueCategoryId]);

    // Close edit mode if originalChart changes (on save)
    useEffect(() => {
        setEditMode(false);
        setSelectedIssue(null);
        setPatientIssueDetailsView(false);
        setCardChart(null);
        setNewPatientIssueId(null);
        setFollowUpDays(null);
    }, [chartContext.originalChart]);

    // Sync this card's edit mode with ChartContext for ChartNavigationBar display
    useEffect(() => {
        chartContext.setCardEditMode?.(className, editMode);
        return () => chartContext.setCardEditMode?.(className, false);
    }, [editMode, chartContext.setCardEditMode]);

    // Auto-open the latest unsaved feedback draft (negative id).
    useEffect(() => {
        const draftIssues = Object.values(chartContext.chart.patientIssue || {})
            .filter((issue) => issue.id < 0 && issue.patientId === chartContext.patientId)
            .sort((a, b) => a.id - b.id);

        const newestDraftIssue = draftIssues[0];
        if (!newestDraftIssue) {
            activeDraftIssueIdRef.current = null;
            return;
        }

        const isAlreadyFocusedOnDraft =
            activeDraftIssueIdRef.current === newestDraftIssue.id &&
            newPatientIssueId === newestDraftIssue.id &&
            editMode &&
            !selectedIssue &&
            !patientIssueDetailsView;

        if (isAlreadyFocusedOnDraft) {
            return;
        }

        activeDraftIssueIdRef.current = newestDraftIssue.id;
        setSelectedIssue(null);
        setPatientIssueDetailsView(false);
        setNewPatientIssueId(newestDraftIssue.id);
        setCardChart({ ...newestDraftIssue });
        setFollowUpDays(null);
        setEditMode(true);
    }, [
        chartContext.chart.patientIssue,
        chartContext.patientId,
        newPatientIssueId,
        editMode,
        selectedIssue,
        patientIssueDetailsView,
    ]);

    useEffect(() => {
        const issues = Object.values(chartContext.originalChart.patientIssue || {})
            .filter(issue => {
                // If there is no orderId, show all feedback for the patient
                if (!chartContext.orderId) {
                    return true;
                }
                // Otherwise, filter by orderId
                return issue.orderId === chartContext.orderId;
            })
            .sort((a, b) => {
                // Sort closed issues to the end
                if (a.issueStatus === 1 && b.issueStatus !== 1) return 1;
                if (a.issueStatus !== 1 && b.issueStatus === 1) return -1;
                // Otherwise sort by date descending
                return new Date(b.issueLoggedOn) - new Date(a.issueLoggedOn);
            });

        const allNotes = Object.values(chartContext.originalChart.patientIssueNote || {});

        const issuesWithNotes = issues.map(issue => ({
            ...issue,
            notes: allNotes
                .filter(note => note.patientIssueId === issue.id)
                .sort((a, b) => new Date(b.noteAddedOn) - new Date(a.noteAddedOn))
        }));

        setPatientIssues(issuesWithNotes);
    }, [chartContext.orderId, chartContext.originalChart.patientIssueNote]);

    /**
     * Returns the styles for a container that should be full width at a given max width.
     * Only used when sidebar=true to handle responsive container queries.
     * @param {number} maxWidth - The max width at which the container should be full width.
     * @returns {Object} The styles for the container.
     */
    const getContainerQuery = (maxWidth) => {
        if (!props.sidebar) {
            return {}
        }

        return {
            [`@container (max-width: ${maxWidth}px)`]: { flexBasis: '100% !important', maxWidth: '100% !important' }
        }
    }

    /**
     * Renders the edit view displaying patient contact information.
     * @returns {JSX.Element} Grid layout with patient information display
     */
    const renderEditView = () => {
        const getIssueReasonOptions = () => {
            return Object.values(mainContext.issueReasonsIndexed)
                .sort((a, b) => {
                    const categoryA = IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[a.issueCategoryId]) || "";
                    const categoryB = IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[b.issueCategoryId]) || "";
                    const categoryComparison = categoryA.localeCompare(categoryB);
                    if (categoryComparison !== 0) {
                        return categoryComparison;
                    }
                    const reasonA = IssueReason.getDisplayName(a) || "";
                    const reasonB = IssueReason.getDisplayName(b) || "";
                    return reasonA.localeCompare(reasonB);
                })
                .map(reason => ({
                value: reason.id,
                label: `${IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[reason.issueCategoryId])} - ${IssueReason.getDisplayName(reason)}`
            }));
        }

        const getComplimentedUserOptions = () => {
            return User.sort(Object.values(mainContext.usersIndexed).filter(user => user.active === true)).map((user, index) => ({
                value: user.id,
                label: User.getDisplayName(user),
                key: index,// Use ID as key since it should be unique and we have multiple dublicate products
            }));
        }

        // Get all orders for the patient
        const getOrderOptions = () => {
            const orderActivities = Object.values(chartContext.chart.orderActivity || {});
            const orderActivityMap = chartContext.chart.orderActivity || {};
            const activityMap = chartContext.chart.activity || {};
            const groupedOrders = {};

            const getStatusLabel = (orderActivity) => {
                const orderStatusReason = mainContext.orderStatusReasonsIndexed?.[orderActivity.orderStatusReasonId];
                return OrderStatusReason.getDisplayName(orderStatusReason)
                    || mainContext.orderStatusesIndexed?.[orderActivity.status]?.name
                    || "Unknown Status";
            };

            const getDisplayTitle = (orderActivity) => {
                if (orderActivity?.orderType === "New") {
                    const products = OrderActivity.getProducts(orderActivity, chartContext.chart);
                    const primaryProduct = OrderActivity.getPrimaryProduct(products);
                    const productName = OrderActivity.getProductDisplayName(
                        primaryProduct,
                        mainContext.baseUnitsIndexed,
                        mainContext.vendorsIndexed,
                        mainContext.productSubTypesIndexed
                    );

                    return productName || "New Order";
                }

                const orderType = orderActivity?.orderType || activityMap[orderActivity?.id]?.type || "Order";
                if (orderType.toLowerCase().includes("ticket")) {
                    return orderType;
                }
                return `${orderType} Ticket`;
            };

            for (const orderActivity of orderActivities) {
                const serviceReason = OrderActivity.getServiceOrderReason(orderActivity, chartContext.chart);
                const activity = activityMap[orderActivity.id];
                const fallbackParentId = activity?.type === "Order" ? orderActivity.id : null;
                const parentOrder = serviceReason?.previousOrderId
                    ? OrderActivity.getParentOrder(serviceReason.previousOrderId, chartContext.chart)
                    : null;
                const parentId = parentOrder?.id || fallbackParentId || orderActivity.id;

                if (!groupedOrders[parentId]) {
                    const parentOrderActivity = orderActivityMap[parentId] || parentOrder || orderActivity;
                    groupedOrders[parentId] = {
                        parent: parentOrderActivity,
                        children: []
                    };
                }

                if (orderActivity.id !== parentId) {
                    groupedOrders[parentId].children.push(orderActivity);
                }
            }

            const sortedParents = OrderActivity.sort(
                Object.values(groupedOrders)
                    .map((group) => group.parent)
                    .filter(Boolean)
            );

            const orderOptions = [];
            for (const parent of sortedParents) {
                const group = groupedOrders[parent.id];
                const parentTitle = getDisplayTitle(parent);
                const parentStatus = getStatusLabel(parent);
                const parentMetaLine = `Order #${parent.id} • ${parentStatus}`;
                orderOptions.push({
                    value: parent.id,
                    label: `${parentTitle} - ${parentMetaLine}`,
                    inputLabel: `#${parent.id} • ${parentTitle}`,
                    searchLabel: `${parentTitle} ${parent.orderType || ""} #${parent.id} ${parentStatus}`,
                    orderType: parent.orderType,
                    title: parentTitle,
                    metaLine: parentMetaLine,
                    statusLabel: parentStatus,
                    level: 0
                });

                const sortedChildren = [...(group?.children || [])].sort((a, b) => {
                    if (a.status === 4 && b.status !== 4) return 1;
                    if (a.status !== 4 && b.status === 4) return -1;
                    const aDate = activityMap[a.id]?.startedAt;
                    const bDate = activityMap[b.id]?.startedAt;
                    return new Date(bDate) - new Date(aDate);
                });

                for (const child of sortedChildren) {
                    const childTitle = getDisplayTitle(child);
                    const childStatus = getStatusLabel(child);
                    const childMetaLine = `Order #${child.id} • ${childStatus}`;
                    orderOptions.push({
                        value: child.id,
                        label: `${childTitle} - ${childMetaLine}`,
                        inputLabel: `#${child.id} • ${childTitle}`,
                        searchLabel: `${childTitle} ${child.orderType || ""} #${child.id} ${childStatus}`,
                        orderType: child.orderType,
                        title: childTitle,
                        metaLine: childMetaLine,
                        statusLabel: childStatus,
                        level: 1
                    });
                }
            }

            return orderOptions;
        };

        const orderOptions = getOrderOptions();
        const selectedOrderOption = orderOptions.find(option => option.value === patientIssue?.orderId) || null;

        return (
            <MUIGrid container spacing={4} rowSpacing={2} sx={{ containerType: 'inline-size' }}>
                <MUIGrid item lg={8} md={12} xs={12} sx={getContainerQuery(600)}>
                    <MUIAutocomplete
                        options={getIssueReasonOptions()}
                        value={getIssueReasonOptions().find(option => option.value === patientIssue?.issueReasonId) || null}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        getOptionLabel={(option) => option?.label || ''}
                        onChange={(e, newValue) => {
                            const selectedReason = mainContext.issueReasonsIndexed[newValue?.value];
                            const nextIssueCategoryId = selectedReason?.issueCategoryId || null;
                            chartContext.onChange({ patientIssue: { [newPatientIssueId]: { issueReasonId: newValue?.value || null, issueCategoryId: nextIssueCategoryId } } })
                            setCardChart({ ...cardChart, issueReasonId: newValue?.value || null, issueCategoryId: nextIssueCategoryId })
                        }}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                variant="standard"
                                label="Reason"
                                fullWidth
                                required={true}
                                error={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.issueReasonId ? true : false}
                                helperText={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.issueReasonId ? chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.issueReasonId : ""}
                            />
                        )}
                    />
                </MUIGrid>
                <MUIGrid item lg={4} md={6} xs={12} sx={getContainerQuery(600)}>
                    <MUIAutocomplete
                        options={orderOptions}
                        value={selectedOrderOption}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        getOptionLabel={(option) => option?.inputLabel || option?.label || ''}
                        onChange={(e, newValue) => {
                            chartContext.onChange({ patientIssue: { [newPatientIssueId]: { orderId: newValue?.value || null } } });
                            setCardChart({ ...cardChart, orderId: newValue?.value || null });
                        }}
                        renderOption={(props, option) => {
                            const IconComponent = MUIIcons[OrderActivityType.getIconName(option?.orderType)] || MUIIcons.Workspaces;
                            return (
                                <li {...props}>
                                    <MUIStack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                        sx={{ width: "100%", pl: option.level === 1 ? 3 : 0 }}
                                    >
                                        <IconComponent fontSize="small" sx={{ mt: 0.25 }} />
                                        <MUIStack spacing={0} sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <MUITypography variant="body2" noWrap>
                                                {option.title}
                                            </MUITypography>
                                            <MUITypography variant="caption" color="text.secondary" noWrap>
                                                {option.metaLine}
                                            </MUITypography>
                                        </MUIStack>
                                    </MUIStack>
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                variant="standard"
                                label="Order"
                                fullWidth
                                required={true}
                                error={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.orderId ? true : false}
                                helperText={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.orderId ? chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.orderId : ""}
                            />
                        )}
                    />
                </MUIGrid>
                {issueCategoryId === 3 ? (
                    <MUIGrid item lg={4} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUIAutocomplete
                            options={getComplimentedUserOptions()}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                chartContext.onChange({ patientIssue: { [newPatientIssueId]: { complimentedUserId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, complimentedUserId: newValue?.value || null })
                            }}
                            getOptionKey={option => option.key} // Must use this when we have multiple dublicate products to prevent console warnings
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Complimented User"
                                    fullWidth
                                    required={true}
                                    error={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.complimentedUserId ? true : false}
                                    helperText={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.complimentedUserId ? chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.complimentedUserId : ""}
                                />
                            )}
                        />
                    </MUIGrid>
                ) : null}
                <MUIGrid item xs={12} sx={getContainerQuery(600)}>
                    <MUITextField
                        multiline
                        rows={2}
                        size="small"
                        label="Description"
                        variant="standard"
                        fullWidth={true}
                        inputProps={{
                            style: { resize: "both" },
                        }}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                            chartContext.onChange({ patientIssue: { [newPatientIssueId]: { complaintDescription: e.target.value } } })
                            setCardChart({ ...cardChart, complaintDescription: e.target.value })
                        }}
                        required={true}
                        error={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.complaintDescription ? true : false}
                        helperText={chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.complaintDescription ? chartContext.validationIssues[className]?.patientIssue?.[newPatientIssueId]?.complaintDescription : ""}
                    />
                </MUIGrid>
            </MUIGrid>
        )
    }

    const renderPatientIssueDetailsView = () => {
        const getIssueReasonOptions = () => {
            return Object.values(mainContext.issueReasonsIndexed)
                .sort((a, b) => {
                    const categoryA = IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[a.issueCategoryId]) || "";
                    const categoryB = IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[b.issueCategoryId]) || "";
                    const categoryComparison = categoryA.localeCompare(categoryB);
                    if (categoryComparison !== 0) {
                        return categoryComparison;
                    }
                    const reasonA = IssueReason.getDisplayName(a) || "";
                    const reasonB = IssueReason.getDisplayName(b) || "";
                    return reasonA.localeCompare(reasonB);
                })
                .map(reason => ({
                value: reason.id,
                label: `${IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[reason.issueCategoryId])} - ${IssueReason.getDisplayName(reason)}`
            }));
        }
        const getAssignableUsersForIssueCategory = (issueCategoryId) => {
            const complianceUsers = User.getComplianceUsers(mainContext.usersIndexed, mainContext.teamsIndexed, mainContext.teamUsersIndexed);
            const customerServiceUsers = User.getCustomerServiceUsers(mainContext.usersIndexed, mainContext.teamsIndexed, mainContext.teamUsersIndexed);

            return issueCategoryId === 1 ? complianceUsers : customerServiceUsers;
        };
        const getAssignToUserOptions = () => {
            const patientIssue = chartContext.chart.patientIssue[selectedIssue.id];
            const issueCategoryId = mainContext.issueReasonsIndexed[patientIssue?.issueReasonId]?.issueCategoryId ?? null;
            const usersToReturn = getAssignableUsersForIssueCategory(issueCategoryId);

            return usersToReturn.map((user, index) => ({
                value: user.id,
                label: user.name,
                key: index,
            }));
        }

        const getUserOptions = () => {
            return User.sort(Object.values(mainContext.usersIndexed).filter(user => user.active === true)).map((user, index) => ({
                value: user.id,
                label: User.getDisplayName(user),
                key: index,// Use ID as key since it should be unique and we have multiple duplicate products
            }));
        }

        const getStatusOptions = () => {
            return IssueStatus.sort(Object.values(mainContext.issueStatusesIndexed)).map(status => ({
                value: status.id,
                label: status.name
            }));
        }

        const patientIssue = chartContext.chart.patientIssue[selectedIssue.id];
        const notes = selectedIssue?.notes;

        const handleNoteChange = (note) => {
            setSelectedIssueNote(note);
            const trimmedNote = note.trim();
            const patientIssueNote = {
                [newPatientIsssueNoteId]: {
                    id: newPatientIsssueNoteId,
                    noteText: note,
                    noteAddedBy: mainContext.currentUser.username,
                    noteAddedOn: new Date().toISOString(),
                    patientIssueId: selectedIssue.id
                }
            }
            // Auto-set followup only when a note exists, the issue is
            // non-compliance, and the current user is eligible in the Assign To
            // list for that category.
            if (
                trimmedNote !== "" &&
                issueCategoryId !== 1 &&
                getAssignableUsersForIssueCategory(issueCategoryId)
                    .some((user) => user.id === mainContext.currentUser.id)
            ) {
                chartContext.onChange({ patientIssueNote: patientIssueNote });
                setFollowUpDays(2);
                const followupDate = Calendar.addDays({
                    daysToAdd: 2,
                    useBusinessDays: true
                });
                chartContext.onChange({ patientIssue: { [patientIssue.id]: { nextFollowupDate: followupDate } } });
                setCardChart({ ...cardChart, patientIssueNote: patientIssueNote, nextFollowupDate: followupDate });
                return;
            }

            if (trimmedNote !== "") {
                chartContext.onChange({ patientIssueNote: patientIssueNote });
                setCardChart({
                    ...cardChart,
                    patientIssueNote: patientIssueNote
                });
                return;
            }

            chartContext.onChange({ patientIssueNote: { [newPatientIsssueNoteId]: undefined } });
            if (issueCategoryId !== 1) {
                setFollowUpDays(null);
            }
        }

        return (
            <MUIGrid container spacing={2} rowSpacing={2} sx={{ containerType: 'inline-size' }}>
                <MUIGrid item xs={12} sx={getContainerQuery(600)}>
                    <MUITypography variant="body2" color="text.secondary">
                        Created {moment.utc(patientIssue?.issueLoggedOn).local().format('M/D/YYYY')} by {User.getDisplayName(mainContext.usersIndexed[patientIssue?.issueLoggedById]) || "Unknown"}
                    </MUITypography>
                </MUIGrid>
                <MUIGrid item xs={12} sx={{ mb: 1 }}>
                    <InfoItem
                        label="Description"
                        value={selectedIssue.complaintDescription}
                    />
                </MUIGrid>
                <MUIGrid item lg={5} md={12} xs={12} sx={getContainerQuery(600)}>
                    <MUIAutocomplete
                        options={getIssueReasonOptions()}
                        value={getIssueReasonOptions().find(option => option.value === patientIssue?.issueReasonId)}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        getOptionLabel={(option) => option?.label || ''}
                        disabled={patientIssue?.issueStatus === 1}
                        onChange={(e, newValue) => {
                            const selectedReason = mainContext.issueReasonsIndexed[newValue?.value];
                            const issueCategoryId = selectedReason?.issueCategoryId || null;
                            chartContext.onChange({ patientIssue: { [patientIssue.id]: { issueReasonId: newValue?.value || null, issueCategoryId } } })
                            setCardChart({ ...cardChart, issueReasonId: newValue?.value || null, issueCategoryId })
                        }}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                variant="standard"
                                label="Reason"
                                fullWidth
                            />
                        )}
                    />
                </MUIGrid>
                <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                    <MUIAutocomplete
                        options={getAssignToUserOptions()}
                        value={getAssignToUserOptions().find(option => option.value === patientIssue?.assignedToId) || null}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        getOptionLabel={(option) => option?.label || ''}
                        getOptionKey={option => option.key} // Must use this when we have multiple duplicate products to prevent console warnings
                        disabled={patientIssue?.issueStatus === 1}
                        onChange={(e, newValue) => {
                            chartContext.onChange({ patientIssue: { [patientIssue.id]: { assignedToId: newValue?.value || null } } })
                            setCardChart({ ...cardChart, assignedToId: newValue?.value || null })
                        }}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                variant="standard"
                                label="Assign To"
                                fullWidth
                            />
                        )}
                    />
                </MUIGrid>
                <MUIGrid item lg={2} md={6} xs={12} sx={getContainerQuery(600)}>
                    {issueCategoryId === 1 ? (
                        <MUIDatePicker
                            label="Followup Date"
                            value={patientIssue?.nextFollowupDate ? moment(patientIssue.nextFollowupDate) : null}
                            disabled={patientIssue?.issueStatus === 1}
                            onChange={(newValue) => {
                                const followupDate = newValue ? newValue.format('YYYY-MM-DD') : null;
                                chartContext.onChange({ patientIssue: { [patientIssue.id]: { nextFollowupDate: followupDate } } });
                                setCardChart({ ...cardChart, nextFollowupDate: followupDate });
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    variant: "standard",
                                    fullWidth: true,
                                    InputLabelProps: { shrink: true },
                                },
                            }}
                        />
                    ) : (
                        <MUITextField
                            type="number"
                            size="small"
                            variant="standard"
                            label="Followup Days"
                                value={followUpDays ?? ""}
                            disabled={patientIssue?.issueStatus === 1}
                            inputProps={{ min: 0, max: 30 }}
                            onChange={(e) => {
                                if (e.target.value === "") {
                                    setFollowUpDays(null);
                                    return;
                                }

                                const days = Math.min(30, Math.max(0, Number(e.target.value)));
                                setFollowUpDays(days);
                                const followupDate = Calendar.addDays({
                                    daysToAdd: days,
                                    useBusinessDays: true
                                });
                                chartContext.onChange({ patientIssue: { [patientIssue.id]: { nextFollowupDate: followupDate } } });
                                setCardChart({ ...cardChart, nextFollowupDate: followupDate });
                            }}
                            InputLabelProps={{ shrink: true }}
                            helperText={
                                followUpDays === null
                                    ? (
                                        originalPatientIssue?.nextFollowupDate
                                            ? moment(originalPatientIssue.nextFollowupDate).format('ddd, MMM D')
                                            : "No date set"
                                    )
                                    : (originalPatientIssue?.nextFollowupDate ? moment(originalPatientIssue.nextFollowupDate).format('MMM D') + ' → ' : '') +
                                    moment(Calendar.addDays({ daysToAdd: followUpDays, useBusinessDays: true })).format('ddd, MMM D')
                            }
                            fullWidth
                        />
                    )}
                </MUIGrid>
                <MUIGrid item lg={2} md={6} xs={12} sx={getContainerQuery(600)}>
                    <MUIAutocomplete
                        options={getStatusOptions()}
                        value={getStatusOptions().find(option => option.value === patientIssue?.issueStatus) || null}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        getOptionLabel={(option) => option?.label || ''}
                        disabled={patientIssue?.issueStatus === 1}
                        disableClearable
                        onChange={(e, newValue) => {
                            const updates = { issueStatus: newValue?.value ?? null };
                            if (newValue?.value === 1) {
                                updates.issueClosedOn = new Date().toISOString();
                                updates.issueClosedById = mainContext.currentUser.id;
                            }
                            chartContext.onChange({ patientIssue: { [patientIssue.id]: updates } });
                            setCardChart({ ...cardChart, ...updates });
                        }}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                variant="standard"
                                label="Status"
                                fullWidth
                            />
                        )}
                    />
                </MUIGrid>
                {issueCategoryId === 3 && (
                    <MUIGrid item lg={2} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUIAutocomplete
                            options={getUserOptions()}
                            value={getUserOptions().find(option => option.value === patientIssue?.complimentedUserId) || null}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            getOptionKey={option => option.key} // Must use this when we have multiple dublicate products to prevent console warnings
                            disabled={patientIssue?.issueStatus === 1}
                            onChange={(e, newValue) => {
                                chartContext.onChange({ patientIssue: { [patientIssue.id]: { complimentedUserId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, complimentedUserId: newValue?.value || null })
                            }}
                            disableClearable
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Complimented User"
                                    fullWidth
                                />
                            )}
                        />
                    </MUIGrid>
                )}
                <MUIGrid item xs={12} sx={getContainerQuery(600)}>
                    <MUITextField
                        multiline
                        rows={3}
                        size="small"
                        label="Note"
                        variant="standard"
                        fullWidth={true}
                        disabled={patientIssue?.issueStatus === 1}
                        inputProps={{
                            style: { resize: "both" },
                        }}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                            handleNoteChange(e.target.value);
                        }}
                    />
                </MUIGrid>
                <MUIGrid item xs={12} sx={getContainerQuery(600)}>
                    <MUITableContainer sx={{ mt: 3 }}>
                        <MUITable
                            size="small"
                            sx={{
                                '& tbody tr:nth-of-type(odd)': {
                                    backgroundColor: (theme) => theme.palette.action.hover
                                },
                                '& td': {
                                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                    verticalAlign: 'top'
                                }
                            }}
                        >
                            <MUITableBody>
                                {notes.map((note) => (
                                    <MUITableRow key={note.id}>
                                        <MUITableCell sx={{ width: "180px", whiteSpace: 'nowrap' }}>
                                            <MUITypography variant="body2" style={{ fontWeight: 500 }}>{moment.utc(note.noteAddedOn).local().format('M/D/YYYY h:mma')}</MUITypography>
                                            <MUITypography variant="body2">{note.noteAddedBy}</MUITypography>
                                        </MUITableCell>
                                        <MUITableCell>{note.noteText}</MUITableCell>
                                    </MUITableRow>
                                ))}
                            </MUITableBody>
                        </MUITable>
                    </MUITableContainer>
                </MUIGrid>

            </MUIGrid>
        )
    }

    /**
     * Renders the read-only view displaying patient demographic information.
     * @returns {JSX.Element} Grid layout with patient information display
     */
    const renderReadOnlyView = () => {
        if (patientIssueDetailsView) {
            return (
                renderPatientIssueDetailsView()
            )
        }
        return (
            <MUITableContainer>
                <MUITable size="small">
                    <MUITableHead>
                        <MUITableRow>
                            <MUITableCell sx={{ width: "150px" }}>Type</MUITableCell>
                            <MUITableCell sx={{ width: "220px" }}>Reason</MUITableCell>
                            <MUITableCell sx={{ width: "180px" }}>Assigned To</MUITableCell>
                            <MUITableCell sx={{ width: "100px" }}>Order #</MUITableCell>
                            <MUITableCell sx={{ width: "100px" }}>Date</MUITableCell>
                            <MUITableCell>Description</MUITableCell>
                        </MUITableRow>
                    </MUITableHead>
                    <MUITableBody>
                        {patientIssues.map((issue) => {
                            // Cuts off at 150-170 characters. Prevents adding
                            // ellipsis for just a couple characters.
                            const allowedExtraCharacters = 20;
                            let sliceLength = 150;
                            if (issue.complaintDescription?.length < sliceLength + allowedExtraCharacters) {
                                sliceLength += allowedExtraCharacters;
                            }
                            const issueCategoryId = mainContext.issueReasonsIndexed[issue.issueReasonId]?.issueCategoryId ?? null;
                            return (
                                <MUITableRow
                                    sx={{ cursor: "pointer" }}
                                    key={issue.id}
                                    onClick={() => {
                                        setSelectedIssue(issue);
                                        setPatientIssueDetailsView(true);
                                        setNewPatientIsssueNoteId(chartContext.getNegativeId());
                                        setFollowUpDays(null);
                                    }}>
                                    <MUITableCell>
                                        <MUIStack direction="row" spacing={2} alignItems="center">
                                            {issueCategoryId === 1 && <MUIReportIcon fontSize="small" sx={{ color: "secondary.main" }} />}
                                            {issueCategoryId === 2 && <MUIThumbDownIcon fontSize="small" sx={{ color: "secondary.main" }} />}
                                            {issueCategoryId === 3 && <MUIThumbUpIcon fontSize="small" sx={{ color: "secondary.main" }} />}
                                            {issueCategoryId === 7 && <MUIHourglassTopIcon fontSize="small" sx={{ color: "secondary.main" }} />}
                                            {issueCategoryId === 8 && <MUIErrorIcon fontSize="small" sx={{ color: "secondary.main" }} />}
                                            <MUITypography variant="body2">{IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[issueCategoryId])}</MUITypography>
                                        </MUIStack>
                                    </MUITableCell>
                                    <MUITableCell>{IssueReason.getDisplayName(mainContext.issueReasonsIndexed[issue.issueReasonId])}</MUITableCell>
                                    <MUITableCell>
                                        {issue.issueStatus === 1 ? (
                                            <MUIStack direction="row" spacing={1} alignItems="center">
                                                <MUITypography variant="body2">Resolved</MUITypography>
                                                <MUICheckIcon fontSize="small" />
                                            </MUIStack>
                                        ) : (
                                            issue.assignedToId ? User.getDisplayName(mainContext.usersIndexed[issue.assignedToId]) : ""
                                        )}
                                    </MUITableCell>
                                    <MUITableCell>{issue.orderId}</MUITableCell>
                                    <MUITableCell>{moment.utc(issue.issueLoggedOn).local().format('M/D/YYYY')}</MUITableCell>
                                    <MUITableCell>{(issue.complaintDescription ?? "").slice(0, sliceLength) + ((issue.complaintDescription ?? "").length > sliceLength ? "..." : "")}</MUITableCell>
                                </MUITableRow>
                            );
                        })}
                    </MUITableBody>
                </MUITable>
            </MUITableContainer>
        )
    }
    /**
 * Determines which view to render based on edit mode state.
 * @returns {JSX.Element} Either edit view or read-only view
 */
    const renderContent = () => {
        if (editMode) {
            return (
                renderEditView()
            )
        }
        return (
            patientIssues?.length > 0 ?
                renderReadOnlyView() :
                (<EmptyState line1="No feedback" line2="Create a feedback to get started" />)
        );
    }

    /**
     * Returns the action buttons for the card component.
     * @returns {Array} Array of action button components
     */
    const getActions = () => {
        const handleCancel = () => {
            if (selectedIssue) {
                const updatedPatientIssue = { ...patientIssue };
                const patientIssueFields = cardChart ? Object.keys(cardChart) : [];

                for (const field of patientIssueFields) {
                    if (patientIssue[field] !== originalPatientIssue?.[field]) {
                        updatedPatientIssue[field] = originalPatientIssue?.[field];
                    }
                }
                chartContext.onChange({ patientIssue: { [selectedIssue.id]: { ...updatedPatientIssue } } });

                const clearedIssues = { ...chartContext.validationIssues };
                delete clearedIssues[className];
                chartContext.updateValidationIssues(clearedIssues);

                // Delete the patientIssueNote with id newPatientIsssueNoteId if it exists
                const updatedChart = { ...chartContext.chart };
                if (updatedChart.patientIssueNote && updatedChart.patientIssueNote[newPatientIsssueNoteId]) {
                    delete updatedChart.patientIssueNote[newPatientIsssueNoteId];
                    chartContext.onChange({ patientIssueNote: { ...updatedChart.patientIssueNote } });
                }
            } else {
                // Delete the patientIssue with id newPatientIssueId
                const updatedChart = { ...chartContext.chart };
                delete updatedChart.patientIssue[newPatientIssueId];

                chartContext.onChange({ patientIssue: { ...updatedChart.patientIssue } });
                const clearedIssues = { ...chartContext.validationIssues };
                delete clearedIssues[className];
                chartContext.updateValidationIssues(clearedIssues);
            }
            chartContext.setCardEditMode?.(className, false);
            setEditMode(false);
            setNewPatientIssueId(null);
            setCardChart(null);
            setFollowUpDays(null);
        }

        // Add back if we end up needing this.
        // Also make sure we only export actual feedback I had one time it exported a negative ID new one
        // const handleExportFeedback = () => {
        //     const csvRows = [];
        //     const headers = ["Feedback ID", "Order ID", "Patient ID", "Issue Status", "Complaint Description", "Issue Category", "Issue Reason", "Issue Logged By", "Issue Logged On", "Issue Closed By"]
        //     csvRows.push(headers);
        //     patientIssues.forEach(issue => {
        //         csvRows.push([
        //             issue.id,
        //             chartContext.orderId,
        //             chartContext.patientId,
        //             mainContext.issueStatusesIndexed[issue.issueStatus]?.name,
        //             issue.complaintDescription,
        //             mainContext.issueCategoriesIndexed[issue.issueCategoryId]?.name,
        //             mainContext.issueReasonsIndexed[issue.issueReasonId]?.name,
        //             User.getDisplayName(mainContext.usersIndexed[issue.issueLoggedById]),
        //             moment.utc(issue.issueLoggedOn).local().format('M/D/YYYY h:mma'),
        //             User.getDisplayName(mainContext.usersIndexed[issue.issueClosedByUserId])
        //         ]);

        //         // Add notes if they exist
        //         if (issue.notes && issue.notes.length > 0) {
        //             csvRows.push(["Note ID", "Note Added By", "Note Added On", "Note Text"]);
        //             issue.notes.forEach(note => {
        //                 csvRows.push([
        //                     note.id,
        //                     note.noteAddedBy,
        //                     moment.utc(note.noteAddedOn).local().format('M/D/YYYY h:mma'),
        //                     note.noteText
        //                 ]);
        //             });

        //             csvRows.push([]);
        //         }
        //     });
        //     const filename = `${Patient.getDisplayName(patient)} Feedback - ${moment().format("YYYY-MM-DD h mm a")}.csv`;
        //     Exporter.exportCSV(csvRows, filename);
        // }

        if (patientIssueDetailsView) {
            return [
                <MUIButton
                    color="error"
                    size="small"
                    disabled={chartContext.isUpdating}
                    onClick={() => {
                        setPatientIssueDetailsView(false);
                        setSelectedIssue(null);
                        handleCancel();
                        setSelectedIssueNote("");
                        setFollowUpDays(null);
                    }}
                >
                    Cancel
                </MUIButton>,
            ];
        }

        const handlePrepareNewPatientIssueData = () => {
            const draftIssues = Object.values(chartContext.chart.patientIssue || {})
                .filter((issue) => issue.id < 0 && issue.patientId === chartContext.patientId)
                .sort((a, b) => a.id - b.id);
            const newIssueId = draftIssues[0]?.id || chartContext.getNegativeId();
            const duplicateDraftIds = draftIssues
                .filter((issue) => issue.id !== newIssueId)
                .map((issue) => issue.id);

            setNewPatientIssueId(newIssueId);
            const patientIssue = {
                [newIssueId]: {
                    id: newIssueId,
                    patientId: chartContext.patientId,
                    issueLoggedById: mainContext.currentUser.id,
                    issueLoggedOn: new Date().toISOString(),
                    issueStatus: 0,
                    issueCategoryId: null,
                    issueReasonId: null,
                    orderId: chartContext.orderId,
                    complaintDescription: null,
                    assignedToId: null,
                    complimentedUserId: null,
                    nextFollowupDate: null,
                }
            };

            const patientIssueUpdates = { ...patientIssue };
            duplicateDraftIds.forEach((draftId) => {
                patientIssueUpdates[draftId] = undefined;
            });

            const patientIssueNoteUpdates = {};
            for (const note of Object.values(chartContext.chart.patientIssueNote || {})) {
                if (
                    note.id < 0 ||
                    note.patientIssueId === newIssueId ||
                    duplicateDraftIds.includes(note.patientIssueId)
                ) {
                    patientIssueNoteUpdates[note.id] = undefined;
                }
            }

            const updates = { patientIssue: patientIssueUpdates };
            if (Object.keys(patientIssueNoteUpdates).length > 0) {
                updates.patientIssueNote = patientIssueNoteUpdates;
            }

            chartContext.onChange(updates);
            setCardChart({ ...cardChart, ...patientIssue[newIssueId] });
        }
        return [
            ...(editMode ? [
                <MUIButton
                    color="error"
                    size="small"
                    disabled={chartContext.isUpdating}
                    onClick={handleCancel}
                >
                    Cancel
                </MUIButton>,
                <MUILoadingButton
                    variant="contained"
                    loading={chartContext.isUpdating}
                    disabled={chartContext.isUpdating}
                    onClick={async () => {
                        await chartContext.handleSaveChart();
                    }}
                    startIcon={<MUISaveIcon />}
                    size="small"
                >
                    Create Feedback
                </MUILoadingButton>
            ] : [
                <MUIButton
                    variant="outlined"
                    disabled={chartContext.isUpdating}
                    onClick={() => {
                        setEditMode(true);
                        handlePrepareNewPatientIssueData();
                    }}
                    startIcon={<MUIAddIcon />}
                    size="small"
                >
                    Create Feedback
                </MUIButton>,
                // <Menu menuItems={[
                //     <MUIMenuItem
                //         onClick={handleExportFeedback}
                //     >
                //         <MUIListItemIcon>
                //             <MUIFileDownloadIcon fontSize="small" />
                //         </MUIListItemIcon>
                //         Download Feedback
                //     </MUIMenuItem>
                // ]} />
            ])
        ];
    }

    return (
        <Card
            renderContent={renderContent}
            getActions={getActions}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartFeedback.meta = { className: className, title: title, icon: icon };


